import {Empty, isLoaded} from 'Core/models/Async';
import {
  FilterEnum,
  getExternalUploadDetailsQueue,
  changeExternalUploadQueueDetailsFilters,
  changeSort,
  clearExternalUploadQueueDetailsFilters,
  changeExternalUploadDetailsQueue,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import Column from 'Shared/components/PagedView/Column';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {withDateFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';
import * as O from 'fp-ts/lib/Option';
import { useParams } from 'react-router-dom';
import { Button } from '@material-ui/core';
import { ModalState, showModal } from 'Core/ducks/modal';

const ExternalUploadQueueDetails = () => {
  const {externalUploadDetailsQueue, externalUploadQueueDetailsFilters, sort} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
 
  const dispatch = useDispatch();
  const changeFilter = withValueChangedHook(
    externalUploadQueueDetailsFilters,
    changeExternalUploadQueueDetailsFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: externalUploadQueueDetailsFilters,
  };
  const {fileId} = useParams();

  useEffect(() => {
    if (fileId) {
      dispatch(
        getExternalUploadDetailsQueue(fileId!, undefined, externalUploadQueueDetailsFilters, O.toUndefined(sort))
      );
    }
  }, [dispatch, externalUploadQueueDetailsFilters, sort]);

  useEffect(() => {
    return () => {
      dispatch(clearExternalUploadQueueDetailsFilters());
      dispatch(changeExternalUploadDetailsQueue(new Empty()));
    };
  }, [dispatch]);

  const getProcessReason = (id?: number) => {
    switch (id) {
      case 1:
        return 'Pending Account Setup';
      case 2:
        return 'File Processing Has Begun';
    }
  };

  return (
    <ModularHost>
      <ToolbarHeader title="View History Details">
        <ExportToolbarHeaderIconWithCustomError
          title="View History Details"
          downloadedFileName="Broker History"
          extension={FileExtension.CSV}
          relativeFileLocation={`/enrollmentFiles/${fileId}/externalUploadQueueDetails/csv/${addFilterParameters({
            ...externalUploadQueueDetailsFilters,
            exportToCsv: true,
          })}`}
          totalRecordCount={isLoaded(externalUploadDetailsQueue) ? externalUploadDetailsQueue.Data.totalCount : 0}
        />
      </ToolbarHeader>
      <PagedView
        pagedViewKey="externalUploadDetails"
        data={externalUploadDetailsQueue}
        sort={O.toUndefined(sort)}
        onChangeSort={sort => dispatch(changeSort(sort))}
        loadMore={page => dispatch(getExternalUploadDetailsQueue(fileId!, page, externalUploadQueueDetailsFilters))}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="File Name"
              filterProperties={withStringFilterProperties(filterProperties, 'fileName')}
              default
              sortColumnKey="fileName"
            >
              {enrollmentFiles => enrollmentFiles.fileName}
            </Column>
            <Column
              {...columnProps}
              title="Action Name"
              sortColumnKey="actionName"
              filterProperties={withStringFilterProperties(filterProperties, 'actionName')}
              default
            >
              {enrollmentFiles => enrollmentFiles.actionName!}
            </Column>
            <Column
              {...columnProps}
              title="Date"
              sortColumnKey="actionDateTime"
              filterProperties={withDateFilterProperties(filterProperties, 'actionDateTime')}
              default
            >
              {enrollmentFiles => toFormattedDate(enrollmentFiles.actionDateTime!)}
            </Column>
            <Column
              {...columnProps}
              title="Actioned By"
              sortColumnKey="actionBy"
              filterProperties={withStringFilterProperties(filterProperties, 'actionBy')}
              default
            >
              {enrollmentFiles => enrollmentFiles.actionBy!}
            </Column>
            <Column
              {...columnProps}
              title="Additional Details"
              default
            >
              {enrollmentFiles => (enrollmentFiles.actionName !== 'Download' && enrollmentFiles.actionName !== 'Complete' && enrollmentFiles.actionName !== 'Cancelled') ? (
                <Button
                  color="primary"
                  onClick={() =>
                    dispatch(
                      showModal(
                        ModalState.ExternalUploadQueueAdditionalDetails({
                          reason: enrollmentFiles.actionName === 'Process' ? getProcessReason(enrollmentFiles.processReason?.id): enrollmentFiles.removalReason!,
                          reasonType: enrollmentFiles.actionName === 'Process' ? 'Process Reason' : 'Return to Broker Reason',
                          returnComments: enrollmentFiles.returnComments!
                        })
                      )
                    )
                  }
                >
                 View
                </Button>
              ) :
              (
                <Button color="primary" disabled>
                 View
                </Button>
              )
              }
            </Column>
          </>
        )}
      </PagedView>
    </ModularHost>
  );
};

export default ExternalUploadQueueDetails;
